/**
 * **连线的列表操作** —— 我们自己实现（原来用 `@vue-flow/core` 的 `addEdge`）。
 *
 * 库那一版做两件事：补 id（没有就按 `e:源.端口->目标.端口` 生成）、
 * 同 id 已存在就**不重复添加**。两件事我们都照抄，但有了单一定义：
 *
 *   · `edgeIdOf()` —— 那条命名约定原来在**五处**各写了一遍
 *     （手势、宿主、序列化、改接、剪贴板），改一处忘四处迟早出事；
 *   · `addEdge()` —— 去重规则写在库里，谁也说不清"同 id"是按 id 还是按端点。
 *
 * ⚠️ 端口 id 允许是 `null` / `undefined` / `''` —— 三者**等价**
 * （见 `connectionRules.ts` 的 `sameHandle`），所以拼 id 之前先归一。
 */
import type { FlowEdge, FlowConnection } from './flowTypes'

/** 端口 id 的**原文**（不做归一）—— 见 `edgeIdOf` 的注释 */
function handleText(handle: string | null | undefined): string {
  return handle ?? ''
}

/**
 * **连线的命名约定** —— `e:源节点.源端口->目标节点.目标端口`。
 *
 * ⚠️ 它是**文档的一部分**：序列化出来的边 id、改接之后的边 id、
 * 剪贴板的边 id 全都长这样，而"同一条边"的判据就是它。
 * 所以只准从这里生成（原来这条约定在**五处**各写了一遍）。
 *
 * ⚠️ **空端口归一成空串**（`null` / `undefined` / `''` 三者等价）——
 * 和 `connectionRules.sameHandle` 的等价规则**保持一致**。
 * 不归一的话 `${null}` 会渲染成字面量 `"null"`，于是
 * 「同一个端口」在比较时等价、在拼 id 时不等价 —— 那种不一致
 * 会在"同一条边被加了两次"这种地方咬人。
 */
export function edgeIdOf(
  source: string,
  sourceHandle: string | null | undefined,
  target: string,
  targetHandle: string | null | undefined,
): string {
  return `e:${source}.${handleText(sourceHandle)}->${target}.${handleText(targetHandle)}`
}

/** 从一个**带端点的对象**拼 id（`reroute.ts` / 剪贴板那种形状） */
export function edgeIdOfEndpoints(edge: {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
}): string {
  return edgeIdOf(edge.source, edge.sourceHandle, edge.target, edge.targetHandle)
}

/** 从一次拖拽产生的连线（还没有 id）补出带 id 的边 */
export function edgeOfConnection(connection: FlowConnection): FlowEdge {
  return {
    id: edgeIdOfEndpoints(connection),
    source: connection.source,
    sourceHandle: connection.sourceHandle ?? null,
    target: connection.target,
    targetHandle: connection.targetHandle ?? null,
  }
}

/**
 * 往连线表里加一条（**同 id 已存在就不加**，原样返回原数组）。
 *
 * ⚠️ 不做"一个输入只接一条"那种覆盖 —— 那是**规则层**的事
 * （`connectionRules.withoutTargetEdge`），调用方先过滤再加。
 * 这一层只管列表。
 *
 * ⚠️ 用泛型 `<E>` 而不是写死 `FlowEdge`：调用方手里那份常常是**更松的
 * 形状**（`EdgeRef` 的 id 是可选的 —— 它描述的是"可能来自任何地方的一行"）。
 * 要求写死会逼调用方先造一批假 id，而这层只用得到 `id` 一个字段。
 */
export function addEdge<E extends { id?: string }>(edge: E, edges: readonly E[]): E[] {
  if (!edge.id) return edges as E[]
  if (edges.some((existing) => existing.id === edge.id)) return edges as E[]
  return [...edges, edge]
}
